import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/logo1.png';
import './Header.css';

const IconMsg = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>;
const IconUser = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>;
const IconLogout = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>;
const IconPlus = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>;
const IconAgents = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
const IconRequest = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>;
const IconBuilding = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" /><path d="M9 22v-4h6v4M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" /></svg>;
const IconGrid = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="2" /><rect x="14" y="3" width="7" height="7" rx="2" /><rect x="3" y="14" width="7" height="7" rx="2" /><rect x="14" y="14" width="7" height="7" rx="2" /></svg>;
const IconMap = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 8 2 16 6 23 2 23 18 16 22 8 18 1 22 1 6" /><line x1="8" y1="2" x2="8" y2="18" /><line x1="16" y1="6" x2="16" y2="22" /></svg>;
const IconTransactions = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 7h11l-3-3M17 17H6l3 3" /><path d="M18 7l-3 3M6 17l3-3" /></svg>;

function Header({ onMessagesClick, unreadCount = 0 }) {
    const [openMenu, setOpenMenu] = useState(null);
    const headerRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();

    const username = localStorage.getItem('username') || 'کاربر';
    const userProfile = JSON.parse(localStorage.getItem('user_profile') || '{}');
    const isAgent = Boolean(userProfile.is_agent);
    const isDeveloper = Boolean(userProfile.is_developer || userProfile.developer_id);
    const isGISTeam = Boolean(userProfile.is_gis_team || userProfile.groups?.includes('GIS Team'));
    const displayName = [userProfile.first_name, userProfile.last_name].filter(Boolean).join(' ') || username;
    const initials = displayName.trim().slice(0, 1) || 'ک';
    const roleLabel = isAgent ? 'نماینده سایتا' : isDeveloper ? 'سازنده' : 'کاربر سایتا';

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

    const primaryAction = isAgent
        ? { to: '/agents', label: 'پنل نمایندگان', icon: <IconAgents /> }
        : isDeveloper
            ? { to: '/developer-projects', label: 'ثبت پروژه', icon: <IconBuilding /> }
            : { to: '/property-wizard', label: 'ثبت ملک', icon: <IconPlus /> };

    useEffect(() => {
        if (!openMenu) return undefined;
        const closeOnOutsideClick = (event) => {
            if (headerRef.current && !headerRef.current.contains(event.target)) setOpenMenu(null);
        };
        const closeOnEscape = (event) => {
            if (event.key === 'Escape') setOpenMenu(null);
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [openMenu]);

    useEffect(() => setOpenMenu(null), [location.pathname]);

    const handleLogout = () => {
        localStorage.clear();
        setOpenMenu(null);
        navigate('/login', { replace: true });
    };

    const closeMenus = () => setOpenMenu(null);

    return (
        <header ref={headerRef} className={`hdr ${openMenu ? 'hdr-menu-open' : ''}`}>
            <Link to="/" className="hdr-logo" aria-label="صفحه اصلی سایتا">
                <img src={logo} alt="سایتا" />
            </Link>

            <div className="hdr-divider" role="separator" />

            <nav className="hdr-nav" aria-label="ناوبری اصلی">
                <Link to="/search" className={`hdr-nav-link ${isActive('/search') ? 'active' : ''}`}>جستجوی ملک</Link>
                <Link to="/transactions" className={`hdr-nav-link ${isActive('/transactions') ? 'active' : ''}`}>معاملات من</Link>
                <Link to="/developer-profiles" className={`hdr-nav-link ${isActive('/developer-profiles') ? 'active' : ''}`}>سازندگان</Link>
            </nav>

            <div className="hdr-spacer" />

            <div className="hdr-actions">
                <div className="hdr-dropdown-wrap hdr-services-wrap">
                    <button
                        className={`hdr-services-btn ${openMenu === 'services' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setOpenMenu(openMenu === 'services' ? null : 'services')}
                        aria-haspopup="true"
                        aria-expanded={openMenu === 'services'}
                    >
                        <IconGrid />
                        <span>خدمات</span>
                        <span className="hdr-chevron" aria-hidden="true">⌄</span>
                    </button>

                    {openMenu === 'services' && (
                        <div className="hdr-dropdown hdr-services-menu" role="menu" dir="rtl">
                            <div className="hdr-menu-heading">شروع یک مسیر</div>
                            {!isAgent && (
                                <>
                                    <Link to="/property-wizard" className="hdr-dropdown-item" role="menuitem" onClick={closeMenus}>
                                        <IconPlus /><span><strong>ثبت ملک</strong><small>ایجاد پرونده و آگهی ملک</small></span>
                                    </Link>
                                    <Link to="/property-request" className="hdr-dropdown-item" role="menuitem" onClick={closeMenus}>
                                        <IconRequest /><span><strong>درخواست ملک</strong><small>ثبت نیاز خرید یا اجاره</small></span>
                                    </Link>
                                    <Link to="/agent-application" className="hdr-dropdown-item" role="menuitem" onClick={closeMenus}>
                                        <IconAgents /><span><strong>همکاری به‌عنوان نماینده</strong><small>ارسال درخواست همکاری</small></span>
                                    </Link>
                                </>
                            )}
                            {isAgent && (
                                <Link to="/agents" className="hdr-dropdown-item" role="menuitem" onClick={closeMenus}>
                                    <IconAgents /><span><strong>پنل نمایندگان</strong><small>مأموریت‌ها و پرونده‌های کاری</small></span>
                                </Link>
                            )}
                            <Link
                                to={isDeveloper ? '/developer-projects' : '/developer-register'}
                                className="hdr-dropdown-item"
                                role="menuitem"
                                onClick={closeMenus}
                            >
                                <IconBuilding />
                                <span>
                                    <strong>{isDeveloper ? 'مدیریت پروژه‌ها' : 'ثبت‌نام سازندگان'}</strong>
                                    <small>{isDeveloper ? 'پروژه‌ها و واحدهای ساختمانی' : 'ساخت پروفایل حرفه‌ای سازنده'}</small>
                                </span>
                            </Link>
                            {isGISTeam && (
                                <Link to="/gis-platform" className="hdr-dropdown-item" role="menuitem" onClick={closeMenus}>
                                    <IconMap /><span><strong>پلتفرم داده مکانی</strong><small>ویرایش و ورود داده‌های GIS</small></span>
                                </Link>
                            )}

                            <div className="hdr-dropdown-sep hdr-mobile-nav" role="separator" />
                            <div className="hdr-menu-heading hdr-mobile-nav">دسترسی سریع</div>
                            <Link to="/search" className="hdr-dropdown-item hdr-mobile-nav" role="menuitem" onClick={closeMenus}><IconMap /><span><strong>جستجوی ملک</strong></span></Link>
                            <Link to="/transactions" className="hdr-dropdown-item hdr-mobile-nav" role="menuitem" onClick={closeMenus}><IconTransactions /><span><strong>معاملات من</strong></span></Link>
                            <Link to="/developer-profiles" className="hdr-dropdown-item hdr-mobile-nav" role="menuitem" onClick={closeMenus}><IconBuilding /><span><strong>سازندگان</strong></span></Link>
                        </div>
                    )}
                </div>

                <Link to={primaryAction.to} className="hdr-primary-action">
                    {primaryAction.icon}
                    <span>{primaryAction.label}</span>
                </Link>

                {onMessagesClick && (
                    <button
                        className="hdr-icon-btn"
                        type="button"
                        aria-label={`پیام‌ها${unreadCount > 0 ? ` — ${unreadCount} خوانده‌نشده` : ''}`}
                        onClick={onMessagesClick}
                    >
                        <IconMsg />
                        {unreadCount > 0 && <span className="hdr-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                    </button>
                )}

                <div className="hdr-actions-divider" role="separator" />

                <div className="hdr-dropdown-wrap">
                    <button
                        className={`hdr-user-btn ${openMenu === 'user' ? 'active' : ''}`}
                        type="button"
                        onClick={() => setOpenMenu(openMenu === 'user' ? null : 'user')}
                        aria-haspopup="true"
                        aria-expanded={openMenu === 'user'}
                        aria-label="منوی حساب کاربری"
                    >
                        <span className="hdr-avatar" aria-hidden="true">{initials}</span>
                        <span className="hdr-user-copy">
                            <strong>{displayName}</strong>
                            <small>{roleLabel}</small>
                        </span>
                        <span className={`hdr-chevron ${openMenu === 'user' ? 'open' : ''}`} aria-hidden="true">⌄</span>
                    </button>

                    {openMenu === 'user' && (
                        <div className="hdr-dropdown hdr-user-menu" role="menu" dir="rtl">
                            <div className="hdr-dropdown-user">
                                <span className="hdr-avatar hdr-avatar-large" aria-hidden="true">{initials}</span>
                                <span className="hdr-dropdown-user-info">
                                    <strong>{displayName}</strong>
                                    <small>{roleLabel}</small>
                                </span>
                            </div>
                            <Link to="/profile" className="hdr-dropdown-item" role="menuitem" onClick={closeMenus}><IconUser /><span><strong>پنل کاربری</strong><small>پروفایل، املاک و علاقه‌مندی‌ها</small></span></Link>
                            <Link to="/transactions" className="hdr-dropdown-item" role="menuitem" onClick={closeMenus}><IconTransactions /><span><strong>معاملات من</strong><small>پیگیری قراردادها و تراکنش‌ها</small></span></Link>
                            <div className="hdr-dropdown-sep" role="separator" />
                            <button className="hdr-dropdown-item danger" type="button" role="menuitem" onClick={handleLogout}><IconLogout /><span><strong>خروج از حساب</strong></span></button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

export default Header;
